import { ReactNode, useMemo } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import { IItemRecord, IVariant } from "records/item/item";
import me from "models/me";
import { IStore } from "reducers";
import type { IScope } from "reducers/scope";
import { ICart } from "constants/cart";
import { getOrder } from "./build-order";
import { PRODUCTS_FILTER } from "constants/brand-settings";

interface IProps {
    item: IItemRecord;
    children: (props: { variants: IVariant[]; isFetching: boolean }) => ReactNode;
}

interface IReduxProps {
    brand: IScope["brand"];
    variants: IVariant[];
    cart: ICart;
    productsMap: { [id: string]: IItemRecord };
    isFetching: boolean;
}

const VariantsProvider = (props: IProps & IReduxProps) => {
    const { item, brand, variants, isFetching, children } = props;
    const { cart, productsMap } = props;

    const warehousesFilter = useMemo(() => {
        const useOrderWarehouseFiltering =
            brand.getPreference("set_warehouse_for_linesheets") &&
            brand.getPreference("filter_products_by") === PRODUCTS_FILTER.BY_WAREHOUSE_IN_WO;
        const order = getOrder(cart);
        return _.compact([useOrderWarehouseFiltering && order?.selected_warehouse]);
    }, [brand, cart]);

    if (me.isGuestBuyer() || !brand?.hasPreference("group_products") || !item?.hasColorVariants()) {
        return null;
    }

    const availableVariants = item?.getAvailableVariants(productsMap, {
        variants,
        warehouses: warehousesFilter,
    });

    if (brand?.hasPreference("external_color_variants")) {
        return _.size(availableVariants) > 1 || isFetching
            ? children({ variants: availableVariants, isFetching: isFetching && _.isEmpty(availableVariants) })
            : null;
    } else {
        return children({ variants: availableVariants, isFetching: false });
    }
};

const mapStateToProps = (store: IStore, { item }: IProps): IReduxProps => ({
    brand: store.scope.brand,
    variants: store.variants.payload[item?._id] ?? [],
    cart: store.cart,
    productsMap: store.entities.items,
    isFetching: store.variants.isFetching,
});

export default connect(mapStateToProps)(VariantsProvider as any);
